/** The five metafield purposes BYO reads. Owner, namespace and key always come from the org's own mapping row. */
export const BYO_DESIGN_PURPOSES = [
    'design_id',
    'back_design_id',
    'design_variant',
    'front_design_variant',
    'back_design_variant',
];
/** The two product-level purposes (the design IDs); the other three live on the variant. */
export const BYO_PRODUCT_PURPOSES = new Set(['design_id', 'back_design_id']);
/** Why a product cannot use its own design IDs. Each has plain copy (`productSync.json`, `byo.blocker.*`). */
export const BYO_DESIGN_BLOCKERS = [
    /** Neither a front nor a back design ID. */
    'design_id_missing',
    /** A design ID that is not 1 to 10 letters and digits after trim and upper-case. */
    'design_id_unreadable',
    /**
     * No longer emitted (2026-11-04: an empty value takes the fallback letter).
     * Kept so job rows saved before then still read and show their copy.
     */
    'design_variant_missing',
    /** The variant's effective light / dark value is some other text. */
    'design_variant_unreadable',
];
/** Soft outcomes. Never a block. */
export const BYO_DESIGN_WARNINGS = [
    /** Front and back light / dark differ on these variants (R3-9). Copy: "The design variant is different between front and back. Was this intended?" */
    'design_sets_differ',
];
/** A design ID, as the SKU and file names may use it (T-86-19-01: no separators, dots or spaces). */
export const BYO_DESIGN_ID_RE = /^[A-Z0-9]{1,10}$/;
/** The SKU letter for a light / dark metafield value, `null` for empty or None, `undefined` for anything else. */
export function byoDesignSetOf(raw) {
    const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (value === '' || value === 'none')
        return null;
    if (value === 'light')
        return 'L';
    if (value === 'dark')
        return 'D';
    if (value === 'medium')
        return 'M';
    return undefined;
}
/** The light / dark metafield word for a SKU letter (what an update writes, R3-12). */
export const BYO_DESIGN_SET_WORD = Object.freeze({
    L: 'Light',
    D: 'Dark',
    M: 'Medium',
});
function normalizeDesignId(raw) {
    const value = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
    return value === '' ? null : value;
}
/** A side's own value when set (empty or None is not set), else the general `design_variant` (the R3-9 "effective" value). */
function effectiveValue(own, general) {
    return byoDesignSetOf(own) === null ? general : own;
}
/**
 * Read one product's design identity. See the module header for the rules.
 * Blocker order: design ID missing, design ID unreadable, light / dark
 * unreadable (the first that applies wins; the variant blocker lists every
 * variant it concerns).
 */
export function readByoDesignIdentity(input) {
    const front = normalizeDesignId(input.frontDesignId);
    const back = normalizeDesignId(input.backDesignId);
    if (front === null && back === null)
        return { ok: false, reason: 'design_id_missing', variantIds: [] };
    // Both IDs may reach file names later (R3-10), so a bad one blocks even when it is not the SKU's.
    if ((front !== null && !BYO_DESIGN_ID_RE.test(front)) || (back !== null && !BYO_DESIGN_ID_RE.test(back))) {
        return { ok: false, reason: 'design_id_unreadable', variantIds: [] };
    }
    const primarySide = back !== null ? 'back' : 'front';
    const designCode = (back ?? front);
    const hasBothSides = front !== null && back !== null;
    const setByVariant = {};
    const defaulted = [];
    const unreadable = [];
    let badValue;
    const differ = [];
    for (const variant of input.variants) {
        const frontValue = effectiveValue(variant.front, variant.general);
        const backValue = effectiveValue(variant.back, variant.general);
        const primary = byoDesignSetOf(primarySide === 'back' ? backValue : frontValue);
        if (primary === null) {
            setByVariant[variant.variantId] = variant.fallback ?? 'L';
            defaulted.push(variant.variantId);
            continue;
        }
        if (primary === undefined) {
            unreadable.push(variant.variantId);
            badValue ??= (primarySide === 'back' ? backValue : frontValue)?.trim().slice(0, 40);
            continue;
        }
        setByVariant[variant.variantId] = primary;
        if (hasBothSides) {
            // Front empty (and no general) with back set is accepted without a warning.
            const other = byoDesignSetOf(frontValue);
            if (other && other !== primary)
                differ.push(variant.variantId);
        }
    }
    if (unreadable.length > 0) {
        return { ok: false, reason: 'design_variant_unreadable', variantIds: unreadable, ...(badValue ? { badValue } : {}) };
    }
    return {
        ok: true,
        designCode,
        primarySide,
        setByVariant,
        defaultedVariantIds: defaulted,
        warnings: differ.length > 0 ? [{ warning: 'design_sets_differ', variantIds: differ }] : [],
    };
}
/**
 * Settings save refusals (86-19 Task 2). tRPC drops error causes, so the
 * message STARTS with `${marker}:` and the settings control branches on that
 * prefix to show its own translated copy (the `STORE_SKUS_INTAKE_ON_MARKER`
 * precedent).
 */
/** Switching on without the design ID and light / dark mappings. */
export const BYO_MAPPINGS_MISSING_MARKER = 'BYO_MAPPINGS_MISSING';
/** Switching back in production once new products use their own design IDs. */
export const BYO_SWITCH_BACK_LOCKED_MARKER = 'BYO_SWITCH_BACK_LOCKED';
/** Changing the design ID source without the platform admin list, or switching on with store SKUs off. */
export const BYO_SWITCH_NOT_ALLOWED_MARKER = 'BYO_SWITCH_NOT_ALLOWED';
